import React, { useState, useCallback, useEffect, useRef } from 'react'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { useFormContext } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { useGetCommoditiesQuery } from 'services/commodities'
import { useGetDefaultPriceSheetQuery } from 'services/priceSheets'
import Loading from 'components/generic/Loading'
import ScaleViewMaterialCard from 'components/generic/card/ScaleViewMaterialCard'
import Draggable from 'components/generic/drag-and-drop/Draggable'
import Droppable from 'components/generic/drag-and-drop/Droppable'
import { setupScaleViewCards, formatData } from 'utils/setupScaleViewCards'

const SettingsScaleViewMaterials = ({ title, subTitle, subTitle2, path, settingsConfiguration }) => {
  const { setValue, formState } = useFormContext()
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  const itemsRef = useRef([])
  const [currentItems, setCurrentItems] = useState([])
  const currentItemsRef = useRef([])
  const selectedCommodityIdRef = useRef(null)

  const { data: commoditiesData, isLoading: isLoadingCommodities } = useGetCommoditiesQuery({
    pageSize: 1000,
  })

  const { data: defaultPriceSheetData, isFetching: isFetchingDefaultPriceSheetData } = useGetDefaultPriceSheetQuery({})

  const isLoading = isLoadingCommodities || isFetchingDefaultPriceSheetData

  useEffect(() => {
    if (isDirty) return
    if (commoditiesData && defaultPriceSheetData) {
      itemsRef.current = setupScaleViewCards(commoditiesData, settingsConfiguration || [], defaultPriceSheetData)
      setCurrentItems(itemsRef.current)
    }
  }, [commoditiesData, defaultPriceSheetData, settingsConfiguration])

  useEffect(() => {
    currentItemsRef.current = currentItems
  }, [currentItems])

  const handleCardClick = item => {
    if (item.id === 'back') {
      selectedCommodityIdRef.current = null
      setCurrentItems(itemsRef.current)
    } else if (selectedCommodityIdRef.current === null && item.materials) {
      selectedCommodityIdRef.current = item.id
      setCurrentItems([
        {
          id: 'back',
          name: 'Back',
        },
        ...item.materials,
      ])
    }
  }

  const debouncedSetValue = useCallback(
    debounce(() => {
      const formattedData = formatData(itemsRef.current)
      setValue(path, formattedData, { shouldDirty: true })
    }, 300),
    [],
  )

  const saveChanges = (newItems, updateFormData = false) => {
    setCurrentItems(newItems)
    if (selectedCommodityIdRef.current) {
      const commodityIndex = itemsRef.current.findIndex(commodity => commodity.id === selectedCommodityIdRef.current)
      itemsRef.current[commodityIndex].materials = newItems.slice(1)
    } else {
      itemsRef.current = newItems
    }
    if (updateFormData) {
      debouncedSetValue()
    }
  }

  // drag-and-drop helper functions
  const moveItem = (sourceId, destinationId, updateFormData = false) => {
    const sourceIndex = currentItemsRef.current.findIndex(item => item.id === sourceId)
    const destinationIndex = currentItemsRef.current.findIndex(item => item.id === destinationId)
    const newItems = [...currentItemsRef.current]
    const [removedItem] = newItems.splice(sourceIndex, 1)
    newItems.splice(destinationIndex, 0, removedItem)
    saveChanges(newItems, updateFormData)
  }

  // color helper functions
  const updateColor = (sourceId, hexCode) => {
    const sourceIndex = currentItemsRef.current.findIndex(item => item.id === sourceId)
    const newItems = [...currentItemsRef.current]
    newItems[sourceIndex].color = hexCode
    saveChanges(newItems, true)
  }

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      {isLoading ? (
        <Loading localized />
      ) : (
        <DndProvider backend={HTML5Backend}>
          <Grid
            container
            sx={{
              textAlign: 'center',
              flex: '1',
              padding: 1,
              backgroundColor: '#FAFAFA',
              borderRadius: 2,
              marginTop: 2,
            }}
          >
            {currentItems.map(item => (
              <Droppable key={item.id} id={item.id} moveItem={moveItem} item xs={3} padding="4px 8px">
                <Draggable id={item.id}>
                  <ScaleViewMaterialCard
                    isCustomizable
                    buttonData={item}
                    onClick={() => handleCardClick(item)}
                    updateColor={updateColor}
                  />
                </Draggable>
              </Droppable>
            ))}
          </Grid>
        </DndProvider>
      )}
    </>
  )
}

SettingsScaleViewMaterials.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  subTitle2: PropTypes.string,
  path: PropTypes.string.isRequired,
  settingsConfiguration: PropTypes.array,
}

SettingsScaleViewMaterials.defaultProps = {
  subTitle2: '',
  settingsConfiguration: [],
}

export default SettingsScaleViewMaterials
